/**
 * The frame every signed-in page sits in: the header, the page, the tab bar,
 * and the toasts and subscription wall over them, and the claim of a
 * founding link opened by an account that already exists (FoundingClaim)
 * and of a gift code opened while signed out (GiftClaim). After the app has
 * gone back to the gardener's own garden because they are no longer a member
 * of the one they were tending (gardenMembers), a toast says why. A Google
 * Photos pick that outlived its dialog or its tab is brought in here
 * (GooglePhotosResume), whichever page is open. The one Report a problem
 * sheet (ReportProblemSheet) is mounted here, for the two doors that open it, and the one "Which bed did they go
 * into?" sheet (WhichBedHost, D69), for the two doors that move a Seed vault plant out, and the one "Which plant is this photo
 * of?" sheet (MovePhotoHost, B160), for the doors that say Grow filed a photo by itself.
 * The welcome is on Today.
 *
 * Each page fades in when it opens (keyed by pageKey), rather than snapping
 * in; a plant's own address (/plants/12) is the Plants page, so following a
 * link to one plant does not fade or rebuild the garden.
 *
 * Nobody signed in sees the sign-in screen instead of the page, except at "/",
 * where the landing page comes first (showsLanding) and its buttons open the
 * sign-in screen on signing in or signing up (a history entry, so the
 * browser's Back, and the sign-in screen's own Back link, return to the
 * landing page); somebody
 * signed in to Firebase with no Grow account yet finishes signing up; and
 * somebody whose account could not be read (a 500, no connection) sees the
 * standard failure page (ServerFailureScreen, D31), never the sign-up path; when
 * the server's request limit refused every try, that page says the limit's own
 * sentence (accountLimited, B24). A
 * sign-in finished on one of the app's own pages then goes to Today, the
 * home (homePage).
 *
 * @module Layout
 */

import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { TabBar } from './TabBar';
import { TipsProvider } from '@/features/tips/TipsContext';
import { useAuth } from '@/shared/hooks/useAuth';
import LoginPage from '@/shared/components/auth/LoginPage';
import LandingPage, { type LandingEntry } from '@/features/landing/LandingPage';
import { showsLanding } from '@/features/landing/lib/showsLanding';
import { CompleteSignup } from '@/shared/components/auth/CompleteSignup';
import { ServerFailureScreen } from '@/shared/components/ServerFailure';
import { Toaster } from '@/shared/components/ui/toaster';
import { toast } from '@/shared/components/ui/use-toast';
import { takeGardenLeftNote } from '@/shared/api/gardenMembers';
import { SubscriptionWall } from '@/features/billing/components/SubscriptionWall';
import { FoundingClaim } from '@/features/billing/components/FoundingClaim';
import { GiftClaim } from '@/features/gift/components/GiftClaim';
import { GooglePhotosResume } from '@/features/photo-tray/components/GooglePhotosResume';
import { ReportProblemSheet } from '@/shared/components/ReportProblemSheet';
// D69: "Which bed did they go into?", asked from a tick or a stage when a Seed vault plant leaves the vault.
import { WhichBedHost } from '@/features/seed-orders/WhichBedSheet';
import { MovePhotoHost } from '@/shared/components/MovePhotoSheet';
import { ViewAsBanner } from './ViewAsBanner';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { afterSignIn } from '@/shared/lib/homePage';
import { FIREBASE_AUTH_HANDLER_PATH } from '@/config/tunables/ui';

/**
 * Which page an address is, for the fade between pages: the path, except
 * that one plant's address (/plants/12) is the Plants page it scrolls.
 *
 * @param pathname - The address's path.
 * @returns The page's key.
 */
function pageKey(pathname: string): string {
  return /^\/plants\/\d+\/?$/.test(pathname) ? '/plants' : pathname;
}

/**
 * The sign-in page, the end of signing up, or the app's frame around the
 * page asked for, by who is signed in.
 *
 * @returns The page.
 */
export function Layout(): JSX.Element {
  const { currentUser, mysqlUser, accountFailed, accountLimited, accountReference, loading, logout, redirectError } = useAuth();
  const { reactTranslate: t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search, hash, state } = useLocation();
  // Which way into the sign-in page the landing page's visitor chose; null while they are still reading it.
  // It rides in the router's state, so the choice is a history entry: the browser's Back returns to the landing page.
  const entered = (state as { landingEntry?: LandingEntry } | null)?.landingEntry ?? null;

  /**
   * Back to the landing page from the sign-in page: one step back in history
   * when the landing page is the entry before, else the same address without the choice.
   */
  const goBackToLanding = (): void => {
    const position = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (position > 0) navigate(-1);
    else navigate({ pathname, search, hash }, { replace: true });
  };

  // A sign-in just finished: to Today, unless it was on a page somebody was sent to.
  const signedIn = Boolean(currentUser && mysqlUser);
  useEffect(() => {
    if (!signedIn) return;
    const to = afterSignIn(pathname);
    if (to) navigate(to, { replace: true });
    // Once per sign-in, not on every page change after it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signedIn]);

  // Sent back to their own garden by a refusal of the one they were tending: said once.
  useEffect(() => {
    if (signedIn && takeGardenLeftNote()) toast({ title: t('garden.left') });
  }, [signedIn, t]);

  // Check if we're on the redirect handler URL
  const isAuthRedirect = window.location.href.includes(FIREBASE_AUTH_HANDLER_PATH);

  // If loading or we're in the middle of an auth redirect, show a loading indicator
  if (loading || isAuthRedirect) {
    return (
      <div className="min-h-screen w-full bg-background flex items-center justify-center">
        <div className="text-lg text-foreground">
          {isAuthRedirect ? t('auth.completingSignIn') : t('sitewide.loading')}
        </div>
      </div>
    );
  }

  // Nobody signed in at all: the landing page at "/" until they choose a way in, else the sign-in page.
  if (!currentUser) {
    if (!entered && showsLanding({ pathname, state, redirectError })) {
      // Same address (its ?founding or ?ad stays), one more history entry.
      return <LandingPage onEnter={(way) => navigate({ pathname, search, hash }, { state: { landingEntry: way } })} />;
    }
    return <LoginPage initialMode={entered ?? undefined} onBack={entered ? goBackToLanding : undefined} />;
  }

  // The account could not be read: said plainly, and never taken for a new
  // sign-up (which would try to make a second account).
  if (!mysqlUser && accountFailed) {
    return <ServerFailureScreen limited={accountLimited} reference={accountReference} onSignOut={() => { void logout(); }} />;
  }

  // Signed in to Firebase, but no Grow account behind it. This used to fall
  // into the branch above, which sent a signed-in person back to the login
  // page they had just come from — the loop that made signing up impossible.
  if (!mysqlUser) {
    return <CompleteSignup />;
  }

  // If authenticated, show the main layout
  return (
    <TipsProvider>
      <div className="min-h-screen w-full bg-background">
        <Header />
        {/*
          * No margin reserved for the header, because the header is sticky and
          * reserves its own.
          *
          * It used to be fixed — out of the layout entirely — with mt-28 here
          * standing in for its height. The header is 56px tall; the margin was
          * 112px. Nothing connected the two, so the guess had drifted to double,
          * and with py-8 on top the garden opened 168px down an empty page.
          *
          * Sticky pins it to the top exactly as fixed did, and occupies its own
          * space, so the number can never be wrong again.
          */}
        {/*
          * Room at the foot for the tab bar, which is fixed and so takes no
          * space of its own — without this the last plant card sits under it.
          * Above md the bar is not rendered and the padding goes back to normal.
          */}
        <main className="container mx-auto px-4 pb-28 pt-6 md:pb-6 text-foreground">
          <div key={pageKey(pathname)} className="motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200">
            <Outlet />
          </div>
        </main>
        <TabBar />
        <Toaster />
        <SubscriptionWall />
        <FoundingClaim />
        <GiftClaim />
        <GooglePhotosResume />
        <ReportProblemSheet />
        <WhichBedHost />
        <MovePhotoHost />
        <ViewAsBanner />
      </div>
    </TipsProvider>
  );
} 